import React, {useEffect, useState} from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import {graphql} from 'react-relay';
import moment from 'moment';
import {useIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import {useForecastFetchQuery} from '../../../forecast-app/shared/hooks/useForecastFetchQuery';
import TooltipContainer from '../../../forecast-app/shared/components/tooltips/tooltip_container';
import {profilePicSrc} from '../../../directApi';
import Util from '../../../forecast-app/shared/util/util';
import {getApprovalStatusBadge, STATUS_RANK} from './TimesheetsUtil';
import {EDIT_FIELD_LABEL_IDS, formatEditSide, getDisplayReason} from '../audit-log/auditLogDisplay';
import {INDICATOR_COLOR, INDICATOR_COLOR_STYLES} from '../../new-ui/HeaderLabel';
import {CSS_CONSTANTS} from '../../../css_variables';
import {hoverRevealScrollbar} from './scrollbar-mixins';
import {
	CheckCircleIcon,
	CloseCircleIcon,
	FeedbackIcon,
	NoteIcon,
	PencilIcon,
	ProgressAlertIcon,
	ProgressCheckIcon,
	ReloadIcon,
} from './AuditLogIcons';

// Re-exported: the drawer's tests and callers import it from here.
export {getDisplayReason};

// ---------------------------------------------------------------------------
// Relay query — fetches approvalLog lazily when the drawer opens.
// approvalLog is gated behind the timesheet_approval_new_statuses feature flag
// on the server; resolves to [] when the flag is off.
// ---------------------------------------------------------------------------
const ApprovalLogDrawerQuery = graphql`
	query ApprovalLogDrawerQuery($ids: [ID!]!) {
		nodes(ids: $ids) {
			... on TimeRegType {
				id
				date
				year
				month
				day
				minutesRegistered
				approvalStatus
				project {
					id
					name
					projectColor
					companyProjectId
					customProjectId
				}
				idleTime {
					name
					isInternalTime
				}
				task {
					name
					companyTaskId
					project {
						id
						name
						projectColor
						companyProjectId
						customProjectId
					}
				}
				approvalLog {
					id
					batchId
					batchScope {
						dayCount
						periodStart
						periodEnd
					}
					eventType
					approvalType
					fromStatus
					toStatus
					actor {
						id
						firstName
						lastName
						profilePictureId
					}
					actorSource
					reason
					isBypassEdit
					createdAt
					invoice {
						id
					}
					invoiceNumber
					invoiceDeleted
					fieldName
					oldValue
					newValue
					oldLabel
					newLabel
					isMaterial
				}
			}
		}
	}
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
type ApprovalLogActor = {
	id: string;
	firstName: string | null;
	lastName: string | null;
	profilePictureId: string | null;
};

/**
 * The extent of the single user action that wrote this entry.
 *
 * Derived server-side from every row the batch wrote, so it describes the whole
 * action rather than the registration currently on screen: approving a week from
 * one day's row still reports all the days that approval covered. Null whenever
 * batchId is null.
 */
type ApprovalLogBatchScope = {
	/** Distinct calendar days the action touched. Two registrations on one day count once. */
	dayCount: number | null;
	periodStart: string | null;
	periodEnd: string | null;
};

/*
 * The API also carries `periodDayCount` (first to last day inclusive) and
 * `isContiguous` (dayCount === periodDayCount). Both are deliberately not selected
 * or rendered: the span counts CALENDAR days, so any batch crossing a weekend reports
 * a gap that never happened — a Mon–Fri fortnight is 10 working days inside a 12-day
 * span, and Friday plus the following Monday is 2 inside 4. Reporting that as a skip
 * on a compliance surface is worse than staying quiet about which days were covered.
 * If the backend ever counts working days, a "3 of 5" form can come back on top of it.
 */

export type ApprovalLogEntry = {
	id: string | null;
	/**
	 * UUID shared by every row one user action produced. Null on rows written before
	 * batch ids existed and on row-birth entries, so a null means "unknown", not
	 * "this action touched exactly one row".
	 */
	batchId: string | null;
	batchScope: ApprovalLogBatchScope | null;
	eventType: 'STATUS_CHANGE' | 'INVOICE_LINK' | 'INVOICE_UNLINK' | 'FIELD_EDIT' | null;
	approvalType: string | null;
	fromStatus: string | null;
	toStatus: string | null;
	actor: ApprovalLogActor | null;
	actorSource: 'USER' | 'SYSTEM' | null;
	reason: string | null;
	isBypassEdit: boolean | null;
	createdAt: string | null;
	/**
	 * Present only when the viewer holds INVOICE_READ and the invoice still exists.
	 * Used purely as the permission signal for whether to link — the number itself
	 * comes from invoiceNumber, which every viewer receives.
	 */
	invoice: {
		id: string;
	} | null;
	/**
	 * The customer-facing invoice number, visible to every viewer. Null on non-invoice
	 * events and once the invoice has been deleted.
	 */
	invoiceNumber: number | null;
	/** True when this invoice event's invoice has been deleted. Null on other events. */
	invoiceDeleted: boolean | null;
	/** Which field a FIELD_EDIT row records. Null on every other event type. */
	fieldName: string | null;
	/**
	 * Machine values either side of the edit: whole minutes, an ISO date, an entity id,
	 * or the note text. Format before showing — never render these raw.
	 */
	oldValue: string | null;
	newValue: string | null;
	/**
	 * The entity's display name as it stood when the edit happened, so a later rename
	 * cannot rewrite the log. Null for scalar fields.
	 */
	oldLabel: string | null;
	newLabel: string | null;
	/** True for the fields that change what was worked, when, or for how long. */
	isMaterial: boolean | null;
};

/**
 * One row of the timeline.
 *
 * A field edit is one row carrying every diff the save produced, so `edits` holds the
 * FIELD_EDIT entries sharing a batch id. Every other event has an empty `edits` and is
 * described entirely by `entry`.
 */
type DisplayEvent = {
	/** The row the chip, actor and timestamp come from. */
	entry: ApprovalLogEntry;
	/** The diff lines to render under it. Empty for everything except an edit. */
	edits: ApprovalLogEntry[];
	/**
	 * Which registration this event belongs to, and how many share its day and task.
	 * Rendered as "entry 1 of 2" only where total > 1 — a day holding one registration
	 * needs no ordinal.
	 */
	regIndex: number;
	regTotal: number;
};

type TimeRegNode = {
	id: string;
	date: string | null;
	year: number | null;
	month: number | null;
	day: number | null;
	minutesRegistered: number | null;
	approvalStatus: string | null;
	project: {
		id: string;
		name: string | null;
		projectColor: string | null;
		companyProjectId: number | null;
		customProjectId: string | null;
	} | null;
	idleTime: {
		name: string | null;
		isInternalTime: boolean | null;
	} | null;
	task: {
		name: string | null;
		companyTaskId: number | null;
		project: {
			id: string;
			name: string | null;
			projectColor: string | null;
			companyProjectId: number | null;
			customProjectId: string | null;
		} | null;
	} | null;
	approvalLog: ApprovalLogEntry[] | null;
};

type QueryResponse = {
	nodes: (TimeRegNode | {__typename?: string} | null)[];
};

/**
 * One calendar day of one task, however many registrations it holds.
 *
 * A day is one section even when several registrations sit under it: an approval
 * covering both of Monday's registrations is one action, and splitting the day would
 * force that event to either repeat or go missing from one half. Registration identity
 * survives the merge on the rows that need it, as `entry N of M`.
 */
export type DayGroup = {
	dateKey: string; // "2026-06-11" — from the time reg's own date field
	dateLabel: string; // "Wednesday, 11 Jun 2026"
	minutes: number; // summed across every registration on this day
	approvalStatus: string | null; // worst-case status across them
	/** Registrations on this day, ascending by id — creation order, stable under edits. */
	regs: {regId: string; sortKey: number; entries: ApprovalLogEntry[]}[];
	/** Built from `regs` once the day is complete. */
	events: DisplayEvent[];
};

type ProjectGroup = {
	projectId: string;
	projectName: string;
	taskName: string | null;
	projectColor: string | null;
	companyProjectId: number | null;
	customProjectId: string | null;
	approvalStatus: string | null;
	totalMinutes: number;
	dayGroups: DayGroup[];
	/**
	 * Events whose single action covered more than one of the days on screen, lifted out
	 * of the days so one action renders as one row. Empty when nothing spans.
	 */
	spanEvents: DisplayEvent[];
	/** The extent those events cover, e.g. "31 Aug – 4 Sep". Null when there are none. */
	spanLabel: string | null;
};

export type ApprovalLogDrawerProps = {
	isOpen: boolean;
	onClose: () => void;
	personName: string;
	personProfilePictureId: string | null;
	/** "9 Jun – 15 Jun 2026" for week mode, "Wednesday, 11 Jun 2026" for day mode */
	contextLabel: string;
	/** Pre-computed total minutes from already-loaded timeRegistrations */
	totalMinutes: number;
	/** Relay global IDs of time registrations to fetch approvalLog for */
	timeRegIds: string[];
	mode: 'week' | 'day';
	/** Show per-task total hours in each accordion header. Only meaningful on the team page where one person may have multiple tasks. */
	showTaskTotal?: boolean;
};

// ---------------------------------------------------------------------------
// Action config — maps approvalType / eventType → display label + chip appearance
// ---------------------------------------------------------------------------
type ActionConfig = {
	labelId: string;
	chipBg: string;
	chipIcon: string | React.FC<{size?: number}>;
	chipIconColor: string;
};

// Shared chip styles derived from HeaderLabel's INDICATOR_COLOR_STYLES — single source of truth.
const APPROVE_CHIP = {
	chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREEN].bg,
	chipIcon: CheckCircleIcon,
	chipIconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREEN].fg,
};
const SUBMIT_CHIP = {
	chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.BLUE].bg,
	chipIcon: ProgressCheckIcon,
	chipIconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.BLUE].fg,
};
const REVERSE_CHIP = {
	chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.BLUE].bg,
	chipIcon: ReloadIcon,
	chipIconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.BLUE].fg,
};

const ACTION_CONFIG: Record<string, ActionConfig> = {
	APPROVE: {labelId: 'time_approval.audit_log.action.approved', ...APPROVE_CHIP},
	FORCE_APPROVE: {labelId: 'time_approval.audit_log.action.force_approved', ...APPROVE_CHIP},
	DECLINE: {
		labelId: 'time_approval.audit_log.action.declined',
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.RED].bg,
		chipIcon: CloseCircleIcon,
		chipIconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.RED].fg,
	},
	SUBMIT: {labelId: 'time_approval.audit_log.action.submitted', ...SUBMIT_CHIP},
	SUBMIT_TIME_OFF: {labelId: 'time_approval.audit_log.action.submitted', ...SUBMIT_CHIP},
	RESUBMIT: {labelId: 'time_approval.audit_log.action.resubmitted', ...SUBMIT_CHIP},
	UNSUBMIT: {
		labelId: 'time_approval.audit_log.action.unsubmitted',
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.AMBER].bg,
		chipIcon: ProgressAlertIcon,
		chipIconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.AMBER].fg,
	},
	UNAPPROVE: {labelId: 'time_approval.audit_log.action.reversed', ...REVERSE_CHIP},
	REVERSE_APPROVAL: {labelId: 'time_approval.audit_log.action.reversed', ...REVERSE_CHIP},
	INVOICE_LINK: {
		labelId: 'time_approval.audit_log.action.invoiced',
		chipBg: '#CCFBF1',
		chipIcon: NoteIcon,
		chipIconColor: '#0F766E',
	},
	INVOICE_UNLINK: {
		labelId: 'time_approval.audit_log.action.invoice_removed',
		chipBg: '#FFF7ED',
		chipIcon: NoteIcon,
		chipIconColor: '#EA580C',
	},
	// Grey reads as informational, which is what an edit is: it records a change to the
	// registration, not a move through the approval workflow.
	FIELD_EDIT: {
		labelId: 'time_approval.audit_log.action.edited',
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREY].bg,
		chipIcon: PencilIcon,
		chipIconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREY].fg,
	},
	NOTE_ADDED: {
		labelId: 'time_approval.audit_log.action.note_added',
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREY].bg,
		chipIcon: FeedbackIcon,
		chipIconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREY].fg,
	},
};

/**
 * Single source of truth for status-colour → chip appearance.
 * Used by the accordion header chip, day-group chips, and the overall header badge.
 * NOTE: BLUE represents "Submitted" — icon is tick, matching ACTION_CONFIG.SUBMIT.
 * UNAPPROVE/REVERSE_APPROVAL use reload via ACTION_CONFIG directly (not via this map).
 */
const STATUS_CHIP_MAP: Record<
	string,
	{chipBg: string; icon: string | React.FC<{size?: number}>; iconColor: string; textColor: string}
> = {
	[INDICATOR_COLOR.GREEN]: {
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREEN].bg,
		icon: CheckCircleIcon,
		iconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREEN].fg,
		textColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREEN].fg,
	},
	[INDICATOR_COLOR.BLUE]: {
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.BLUE].bg,
		icon: ProgressCheckIcon,
		iconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.BLUE].fg,
		textColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.BLUE].fg,
	},
	[INDICATOR_COLOR.RED]: {
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.RED].bg,
		icon: CloseCircleIcon,
		iconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.RED].fg,
		textColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.RED].fg,
	},
	[INDICATOR_COLOR.AMBER]: {
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.AMBER].bg,
		icon: ProgressAlertIcon,
		iconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.AMBER].fg,
		textColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.AMBER].fg,
	},
	[INDICATOR_COLOR.GREY]: {
		chipBg: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREY].bg,
		icon: NoteIcon,
		iconColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREY].fg,
		textColor: INDICATOR_COLOR_STYLES[INDICATOR_COLOR.GREY].fg,
	},
};

/** PENDING / NOT_SUBMITTED mean "no workflow action yet" — no chip to show. */
const PENDING_STATUSES = new Set(['PENDING', 'NOT_SUBMITTED']);

/**
 * Returns true for entries that represent a real workflow event worth displaying.
 * Filters out system creation artefacts that have neither a recognised approvalType
 * nor a toStatus transition.
 */
const isMeaningfulEntry = (e: ApprovalLogEntry): boolean =>
	(e.approvalType != null && e.approvalType in ACTION_CONFIG) ||
	e.eventType === 'INVOICE_LINK' ||
	e.eventType === 'INVOICE_UNLINK' ||
	e.eventType === 'FIELD_EDIT' ||
	e.toStatus != null;

export const getActionConfig = (entry: ApprovalLogEntry): ActionConfig => {
	// Before the approvalType branch: a field edit carries no approvalType and no status
	// transition, so the toStatus fallback below would mislabel it.
	if (entry.eventType === 'FIELD_EDIT') {
		return entry.fieldName === 'NOTES' ? ACTION_CONFIG.NOTE_ADDED : ACTION_CONFIG.FIELD_EDIT;
	}
	// A correction reads as a correction whichever verb carried it. RESUBMIT used to be the only
	// way out of DECLINED, so the verb alone identified one; now that Submit reaches declined rows
	// too — so a week holding declined and newly logged time is one press, not two — the verb no
	// longer distinguishes them and the transition does. Deliberately ahead of the approvalType
	// lookup, which would otherwise label these SUBMIT / SUBMIT_TIME_OFF and lose the distinction
	// the new statuses were introduced to make.
	//
	// Both endpoints are required. `fromStatus` alone would catch REVERSE_APPROVAL leaving
	// APPROVED, and a null `fromStatus` (rows written before the column was populated) is unknown
	// rather than declined, so it stays an ordinary submission.
	if (entry.fromStatus === 'DECLINED' && entry.toStatus === 'SUBMITTED') {
		return ACTION_CONFIG.RESUBMIT;
	}
	if (entry.approvalType && ACTION_CONFIG[entry.approvalType]) {
		return ACTION_CONFIG[entry.approvalType];
	}
	if (entry.eventType === 'INVOICE_LINK') return ACTION_CONFIG.INVOICE_LINK;
	if (entry.eventType === 'INVOICE_UNLINK') return ACTION_CONFIG.INVOICE_UNLINK;
	// System entries with no approvalType: derive display from toStatus
	if (entry.toStatus) {
		const badge = getApprovalStatusBadge(entry.toStatus);
		if (badge) {
			const c = STATUS_CHIP_MAP[badge.color];
			if (c) {
				return {labelId: badge.labelId, chipBg: c.chipBg, chipIcon: c.icon, chipIconColor: c.iconColor};
			}
		}
	}
	return ACTION_CONFIG.SUBMIT; // last-resort fallback
};

/**
 * What the invoice row should show, if anything:
 *  - `link`    the invoice exists and this viewer holds INVOICE_READ — deep link to it.
 *  - `number`  the invoice exists but the viewer cannot open it. The number identifies
 *              which invoice their time landed on and leaks none of its contents, so it
 *              is shown as plain text; a link would only lead to a page /finance/invoices
 *              denies them.
 *  - `deleted` the event referenced an invoice that has since been deleted. The FK is
 *              ON DELETE SET NULL, so the id and number are both gone and there is
 *              nothing to show — but staying silent would leave the event with no
 *              invoice reference at all, which is worse on an audit trail.
 *  - `null`    not an invoice event.
 *
 * `link` and `number` are driven by `invoice` being present, which the server sets only
 * for viewers with INVOICE_READ — there is no client-side permission check.
 */
const INVOICE_ROW_CY = {
	link: 'audit-log-invoice-link',
	number: 'audit-log-invoice-number',
	deleted: 'audit-log-invoice-deleted',
} as const;

export const getInvoiceDisplay = (
	entry: Pick<ApprovalLogEntry, 'invoice' | 'invoiceNumber' | 'invoiceDeleted'>
): {kind: 'link'; href: string; invoiceNumber: number} | {kind: 'number'; invoiceNumber: number} | {kind: 'deleted'} | null => {
	const {invoiceNumber} = entry;
	if (invoiceNumber != null) {
		return entry.invoice
			? {kind: 'link', href: `/finance/invoices/I-${invoiceNumber}`, invoiceNumber}
			: {kind: 'number', invoiceNumber};
	}
	return entry.invoiceDeleted ? {kind: 'deleted'} : null;
};

/**
 * Format ISO 8601 createdAt → "15 Jun 2026 · 2:14 PM GMT"
 * Full date is shown because the event date may differ from the time reg's own date
 * (e.g., a status change made today on a June 13 entry).
 */
/**
 * Renders the extent of the action that wrote an entry, e.g. "3 days, 11–15 May".
 *
 * Returns null when there is nothing worth saying, which is the common case:
 *
 *  - No batch id (rows written before batch ids existed, and row births) — the
 *    extent is unknown, and an absent qualifier is honest where a "1 day" one
 *    would be a claim the data cannot support.
 *  - Invoice events — whether they are batched on the same unit as an approver
 *    decision is not established, so they are left alone rather than guessed at.
 *  - dayCount <= 1 — the action covered only the day already on screen, so the
 *    qualifier would repeat what the reader can see. It is shown only where the
 *    event's scope differs from the scope of the view the reader is in.
 *
 * The label leads with the count rather than the range: a manager approving Monday,
 * Wednesday and Friday produces a batch whose extent is a set, not a span, and
 * rendering that as "11–15 May" alone would be a quiet falsehood on a compliance
 * surface. The count is exact — distinct days actually touched — and the range bounds
 * them, so "3 days, 11–15 May" holds whether or not the action skipped a day.
 *
 * There is one form, not an "all N" / "N of M" pair, because the only signal that could
 * pick between them counts calendar days (see ApprovalLogBatchScope). A second form
 * would let a Mon–Fri fortnight announce a skip that never happened, and even a bare
 * "10 days" reads as a skip once a reader has seen "all 5 days" elsewhere.
 */
export const getBatchScopeLabel = (
	entry: Pick<ApprovalLogEntry, 'batchId' | 'batchScope' | 'eventType'>,
	intl: ReturnType<typeof useIntl>
): string | null => {
	if (!entry.batchId) return null;
	if (entry.eventType !== 'STATUS_CHANGE') return null;

	const scope = entry.batchScope;
	if (!scope) return null;

	const {dayCount, periodStart, periodEnd} = scope;
	if (dayCount == null || dayCount <= 1) return null;
	if (!periodStart || !periodEnd) return null;

	const start = moment(periodStart, 'YYYY-MM-DD');
	const end = moment(periodEnd, 'YYYY-MM-DD');
	if (!start.isValid() || !end.isValid()) return null;

	// "11–15 May" when one month spans it, "28 Apr – 2 May" when it straddles two.
	const periodRange = start.isSame(end, 'month')
		? `${start.format('D')}–${end.format('D MMM')}`
		: `${start.format('D MMM')} – ${end.format('D MMM')}`;

	return intl.formatMessage({id: 'time_approval.audit_log.batch_scope.days'}, {dayCount, periodRange});
};

/**
 * Fields whose diff the timeline shows. The rest are recorded for reporting but left off
 * the timeline: a person or retainer-conflict change is not what an approver opened this
 * drawer to read.
 *
 * ROLE is shown even though it is classified non-material, because the role a registration
 * is logged against is what the rate card is keyed on — changing it moves what the time is
 * worth. It is the second field, after NOTES, where visibility and materiality diverge on
 * purpose. Shown as its own labelled diff line rather than folded into an unexplained
 * "Edited", so a reader can see a rate-bearing change without opening anything.
 *
 * BILLABLE_MINUTES_REGISTERED is the conditional case — see collapseEditBatch.
 */
const SHOWN_EDIT_FIELDS = new Set(['LOGGED_AGAINST', 'MINUTES_REGISTERED', 'DATE', 'ROLE']);

const isFieldEdit = (e: ApprovalLogEntry): boolean => e.eventType === 'FIELD_EDIT';
const isNoteEdit = (e: ApprovalLogEntry): boolean => isFieldEdit(e) && e.fieldName === 'NOTES';

/**
 * Collapse the fields of one save into the diff lines worth showing.
 *
 * Billable minutes are normally recorded and not shown. The exception is a save that
 * also re-pointed the registration: a move is logged for its billing consequence, and
 * "Logged against: A → B" states the cause without stating the consequence — it does not
 * tell the reader the time stopped being billable. The two merely co-occur in one save;
 * the billable value is client-supplied and is never recomputed from the new task, so do
 * not read the sibling line as derived from the move.
 */
export const collapseEditBatch = (edits: ApprovalLogEntry[]): ApprovalLogEntry[] => {
	const hasLoggedAgainst = edits.some(e => e.fieldName === 'LOGGED_AGAINST');
	return edits.filter(
		e =>
			(e.fieldName != null && SHOWN_EDIT_FIELDS.has(e.fieldName)) ||
			(e.fieldName === 'BILLABLE_MINUTES_REGISTERED' && hasLoggedAgainst)
	);
};

/**
 * Turn a registration's raw log rows into timeline rows.
 *
 * The fields of one save collapse into a single event carrying every diff, because they
 * were one action at one timestamp. Two separate saves never merge, even when they touch
 * the same field — they are two revisions and must read as two.
 *
 * Grouping keys on batchId, falling back to the row's own id when that is null. Rows
 * written before batch ids existed therefore each stand alone rather than collapsing
 * into one bogus "null batch" event.
 *
 * A notes change is split out as its own event: a note is prose, not a before/after
 * pair, and it renders attributed to whoever wrote it.
 */
export const groupEditsByBatch = (entries: ApprovalLogEntry[]): {entry: ApprovalLogEntry; edits: ApprovalLogEntry[]}[] => {
	const out: {entry: ApprovalLogEntry; edits: ApprovalLogEntry[]}[] = [];
	const batchIndex = new Map<string, number>();

	for (const entry of entries) {
		if (!isFieldEdit(entry)) {
			out.push({entry, edits: []});
			continue;
		}

		// A note is its own event, never folded into the save's diff list.
		if (isNoteEdit(entry)) {
			out.push({entry, edits: []});
			continue;
		}

		const key = entry.batchId ?? `__row__${entry.id ?? out.length}`;
		const existing = batchIndex.get(key);
		if (existing == null) {
			batchIndex.set(key, out.length);
			out.push({entry, edits: [entry]});
		} else {
			out[existing].edits.push(entry);
		}
	}

	return out;
};

/** Render one side of a diff in the units a reader thinks in, never the stored string. */
const formatEditValue = (entry: ApprovalLogEntry, side: 'old' | 'new', intl: ReturnType<typeof useIntl>): string =>
	formatEditSide(
		{
			fieldName: entry.fieldName,
			value: side === 'old' ? entry.oldValue : entry.newValue,
			label: side === 'old' ? entry.oldLabel : entry.newLabel,
		},
		intl
	);

/**
 * "15 Jun 2026 · 2:14 PM GMT", or just "2:14 PM GMT" once the date is already on the
 * row above. The date cannot come from the group header: that carries the
 * registration's own date while these carry event timestamps, and a group headed
 * 20 Aug can legitimately hold events stamped 19 Aug.
 */
const formatEntryTime = (createdAt: string | null, showFullDate = true): string => {
	if (!createdAt) return '';
	const stamp = moment.utc(createdAt);
	return (showFullDate ? stamp.format('D MMM YYYY · h:mm A') : stamp.format('h:mm A')) + ' GMT';
};

/** Format minutes → "39h 05m" using existing Util helper. */
const formatMinutes = (minutes: number, intl: ReturnType<typeof useIntl>): string => {
	return Util.convertMinutesToFullHour(minutes, intl, true);
};

/**
 * Build one day's timeline from the registrations under it.
 *
 * Two registrations on one day were submitted, approved and invoiced by the same
 * actions, so those rows appear once per registration and must render once. Dedup keys
 * on batchId — exactly the set of rows one action produced — which also keeps separate
 * edits separate for free: two registrations edited individually carry two batch ids and
 * stay two rows, because merging them would imply a save that never happened and summing
 * their deltas would describe neither.
 *
 * Rows with no batch id are never deduped. A null means "unknown", not "one row".
 */
export const buildDayEvents = (dayGroup: DayGroup): DisplayEvent[] => {
	const regs = [...dayGroup.regs].sort((a, b) => a.sortKey - b.sortKey);
	const regTotal = regs.length;

	const events: DisplayEvent[] = [];
	const seenBatches = new Set<string>();

	regs.forEach((reg, regIndexZeroBased) => {
		const meaningful = reg.entries.filter(isMeaningfulEntry);
		for (const grouped of groupEditsByBatch(meaningful)) {
			const {entry} = grouped;

			// An edit belongs to one registration and is never deduped across them.
			if (entry.eventType !== 'FIELD_EDIT' && entry.batchId) {
				if (seenBatches.has(entry.batchId)) continue;
				seenBatches.add(entry.batchId);
			}

			events.push({
				entry,
				edits: grouped.edits,
				regIndex: regIndexZeroBased + 1,
				regTotal,
			});
		}
	});

	return events.sort((a, b) => {
		const ta = a.entry.createdAt ? new Date(a.entry.createdAt).getTime() : 0;
		const tb = b.entry.createdAt ? new Date(b.entry.createdAt).getTime() : 0;
		return tb - ta;
	});
};

/** "31 Aug – 4 Sep", or "28 Dec 2026 – 1 Jan 2027" when the span crosses a year. */
const formatDaySpan = (dateKeys: string[]): string | null => {
	const sorted = [...dateKeys].sort();
	const start = moment(sorted[0], 'YYYY-MM-DD');
	const end = moment(sorted[sorted.length - 1], 'YYYY-MM-DD');
	if (!start.isValid() || !end.isValid()) return null;
	if (start.isSame(end, 'year')) {
		return start.isSame(end, 'month')
			? `${start.format('D')} – ${end.format('D MMM YYYY')}`
			: `${start.format('D MMM')} – ${end.format('D MMM YYYY')}`;
	}
	return `${start.format('D MMM YYYY')} – ${end.format('D MMM YYYY')}`;
};

/**
 * Lift events out of the days they were duplicated across.
 *
 * One approval of a whole week writes a row against every registration it touched, and
 * those registrations sit in different day sections — so the same action renders once per
 * day, identically timestamped, with nothing to say it was one action. Deduping within a
 * day (buildDayEvents) never catches this: the duplicates are in different days by
 * definition.
 *
 * An event is lifted when its batch id occurs in more than one day section of this task
 * AND in every day section this task has. Both halves matter. The first asks whether the
 * row is actually duplicated on screen, rather than what the batch did elsewhere — a batch
 * covering five days shown in a one-day drawer appears once and stays put, its scope line
 * already stating the wider extent.
 *
 * The second is what keeps the collapse honest. A batch present in every day section is
 * fully accounted for by the band's own span, so nothing is lost by showing it once. A
 * batch present in only some of them is not: collapsing it to a single row would leave a
 * reader unable to tell WHICH sections it touched, and the scope line is deliberately not
 * an expander. So it stays per-day, where the sections it appears in are themselves the
 * answer. Naming the days in the label instead would need the batch's dates on the API,
 * which it does not carry today.
 *
 * Note this predicate is about coverage of the DRAWER, not of the batch's own span: a
 * Mon–Fri fortnight covers every section on screen and is lifted. That is correct, and
 * safe now that the scope line no longer claims a span it counted in calendar days.
 *
 * Never lifted: edits, which belong to one registration on one day, and rows with no batch
 * id, where a null means "unknown" rather than "one action".
 *
 * Days emptied by the lift are dropped. A day that never had events keeps its section, so
 * the "no history yet" placeholder still appears where it is true.
 */
export const hoistSpanningEvents = (group: ProjectGroup): ProjectGroup => {
	const daysByBatch = new Map<string, Set<string>>();
	for (const dayGroup of group.dayGroups) {
		for (const event of dayGroup.events) {
			const batchId = event.entry.batchId;
			if (!batchId || event.entry.eventType === 'FIELD_EDIT') continue;
			if (!daysByBatch.has(batchId)) daysByBatch.set(batchId, new Set());
			(daysByBatch.get(batchId) as Set<string>).add(dayGroup.dateKey);
		}
	}

	const daysWithEvents = group.dayGroups.filter(dayGroup => dayGroup.events.length > 0).length;
	const spanning = new Set(
		Array.from(daysByBatch.entries())
			.filter(([, days]) => days.size > 1 && days.size === daysWithEvents)
			.map(([batchId]) => batchId)
	);
	if (spanning.size === 0) return group;

	const spanEvents: DisplayEvent[] = [];
	const lifted = new Set<string>();
	const emptiedDays = new Set<string>();

	for (const dayGroup of group.dayGroups) {
		dayGroup.events = dayGroup.events.filter(event => {
			const batchId = event.entry.batchId;
			if (!batchId || event.entry.eventType === 'FIELD_EDIT' || !spanning.has(batchId)) {
				return true;
			}
			emptiedDays.add(dayGroup.dateKey);
			if (lifted.has(batchId)) return false;
			lifted.add(batchId);
			spanEvents.push(event);
			return false;
		});
	}

	spanEvents.sort((a, b) => {
		const ta = a.entry.createdAt ? new Date(a.entry.createdAt).getTime() : 0;
		const tb = b.entry.createdAt ? new Date(b.entry.createdAt).getTime() : 0;
		return tb - ta;
	});

	group.spanEvents = spanEvents;
	group.spanLabel = formatDaySpan(
		Array.from(spanning).flatMap(batchId => Array.from(daysByBatch.get(batchId) as Set<string>))
	);
	group.dayGroups = group.dayGroups.filter(dayGroup => dayGroup.events.length > 0 || !emptiedDays.has(dayGroup.dateKey));

	return group;
};

/**
 * Group time regs by project, then within each project create one DayGroup per
 * time reg (keyed by the time reg's own date field, not the event's createdAt).
 * This ensures "June 10 · 15m" reflects what was actually logged on that day.
 *
 * Accordion chip tracks the most recently dated time reg's status in O(n) via
 * Priority uses the same STATUS_RANK as the team timesheet table so the chip always matches the table row.
 */
const groupByProject = (timeRegs: TimeRegNode[]): ProjectGroup[] => {
	const map = new Map<string, ProjectGroup>();

	for (const reg of timeRegs) {
		// For task-based regs, task.project carries the correct customProjectId.
		const project = reg.task?.project ?? reg.project;
		// Key by project+task so that different tasks within the same project get separate sections.
		const taskKey = reg.task?.companyTaskId != null ? `T${reg.task.companyTaskId}` : reg.task?.name ?? null;
		const pid = project?.id
			? taskKey != null
				? `${project.id}__${taskKey}`
				: project.id
			: `__no_project__${reg.idleTime?.name ?? ''}`;

		if (!map.has(pid)) {
			let projectName: string;
			if (project) {
				projectName = project.name ?? 'Unknown';
			} else if (reg.idleTime) {
				projectName = reg.idleTime.name ?? (reg.idleTime.isInternalTime ? 'Internal Time' : 'Time Off');
			} else {
				projectName = 'Unknown';
			}
			map.set(pid, {
				projectId: pid,
				projectName,
				taskName: reg.task?.name ?? null,
				projectColor: project?.projectColor ?? null,
				companyProjectId: project?.companyProjectId ?? null,
				customProjectId: project?.customProjectId ?? null,
				approvalStatus: null,
				totalMinutes: 0,
				dayGroups: [],
				spanEvents: [],
				spanLabel: null,
			});
		}

		const group = map.get(pid) as ProjectGroup;
		group.totalMinutes += reg.minutesRegistered ?? 0;

		// Build the DayGroup for this time reg using its own date (not event dates).
		// Fall back to year/month/day integers when the date string is absent.
		const rawDate: string | null = reg.date
			? reg.date.slice(0, 10)
			: reg.year != null && reg.month != null && reg.day != null
			? `${reg.year}-${String(reg.month).padStart(2, '0')}-${String(reg.day).padStart(2, '0')}`
			: null;
		const dateKey = rawDate ?? `__unknown__${reg.id}`;
		const dateLabel = rawDate ? moment(rawDate, 'YYYY-MM-DD').format('dddd, D MMM YYYY') : 'Unknown date';

		// Sort this reg's log entries newest-first
		const entries = [...(reg.approvalLog ?? [])].sort((a, b) => {
			const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
			const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
			return tb - ta;
		});

		// Ascending numeric id is creation order, and unlike the duration it does not
		// move when someone edits the registration -- which matters here, because the
		// field being audited would otherwise be the field doing the identifying.
		let sortKey = Number.MAX_SAFE_INTEGER;
		try {
			const parsed = Util.getIdFromBase64String(reg.id);
			if (Number.isFinite(parsed)) sortKey = parsed;
		} catch (e) {
			// Not a decodable global id; fall back to arrival order.
		}

		// One section per day, not per registration.
		let dayGroup = group.dayGroups.find(dg => dg.dateKey === dateKey);
		if (!dayGroup) {
			dayGroup = {
				dateKey,
				dateLabel,
				minutes: 0,
				approvalStatus: null,
				regs: [],
				events: [],
			};
			group.dayGroups.push(dayGroup);
		}
		dayGroup.minutes += reg.minutesRegistered ?? 0;
		dayGroup.regs.push({regId: reg.id, sortKey, entries});

		const dayIncoming = reg.approvalStatus ?? 'PENDING';
		const dayIncomingRank =
			(STATUS_RANK as Record<string, number>)[dayIncoming] ?? (STATUS_RANK as Record<string, number>)['PENDING'];
		const dayCurrent = dayGroup.approvalStatus ?? 'INVOICED';
		const dayCurrentRank =
			(STATUS_RANK as Record<string, number>)[dayCurrent] ?? (STATUS_RANK as Record<string, number>)['INVOICED'];
		if (dayIncomingRank < dayCurrentRank) {
			dayGroup.approvalStatus = dayIncoming;
		}

		// Accordion chip = worst-case status across all regs, matching the same priority used by the table row.
		const incoming = reg.approvalStatus ?? 'PENDING';
		const incomingRank =
			(STATUS_RANK as Record<string, number>)[incoming] ?? (STATUS_RANK as Record<string, number>)['PENDING'];
		const current = group.approvalStatus ?? 'INVOICED'; // INVOICED = lowest priority sentinel
		const currentRank =
			(STATUS_RANK as Record<string, number>)[current] ?? (STATUS_RANK as Record<string, number>)['INVOICED'];
		if (incomingRank < currentRank) {
			group.approvalStatus = incoming;
		}
	}

	// Sort day groups newest-first within each project, then build each day's timeline.
	for (const group of map.values()) {
		group.dayGroups.sort((a, b) => b.dateKey.localeCompare(a.dateKey));
		for (const dayGroup of group.dayGroups) {
			dayGroup.events = buildDayEvents(dayGroup);
		}
		hoistSpanningEvents(group);
	}

	// Mirror the sort order of the team timesheet table: companyProjectId asc, then taskName asc.
	return Array.from(map.values()).sort(
		(a, b) =>
			(a.companyProjectId ?? 111111) - (b.companyProjectId ?? 111111) ||
			(a.taskName ?? '').localeCompare(b.taskName ?? '')
	);
};

/** Derive 2-letter initials from first + last name. */
const getInitials = (firstName: string | null, lastName: string | null): string => {
	const f = (firstName ?? '').trim()[0] ?? '';
	const l = (lastName ?? '').trim()[0] ?? '';
	return (f + l).toUpperCase() || '?';
};

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------
const DrawerContainer = styled.div<{isOpen: boolean}>`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	width: 480px;
	transform: ${({isOpen}) => (isOpen ? 'translateX(0)' : 'translateX(100%)')};
	transition: transform 250ms cubic-bezier(0.4, 0, 0.2, 1);
	background: #ffffff;
	border-left: 1px solid #e8e8e8;
	/* Only cast the shadow while open. One drawer is mounted per person row, all
	 * parked off-screen right when closed; a permanent box-shadow on those closed
	 * drawers bleeds ~24px back onto the page and stacks into a dark vertical line
	 * at the right edge (visible only with the FF on). Gating on isOpen removes it
	 * so the page matches the FF-off layout. (PIKA-627) */
	box-shadow: ${({isOpen}) => (isOpen ? '-4px 0 20px rgba(0, 0, 0, 0.08)' : 'none')};
	z-index: 200;
	display: flex;
	flex-direction: column;
	font-family: system-ui, sans-serif;
`;

const DrawerHeader = styled.div`
	padding: 16px 18px 14px;
	border-bottom: 1px solid #f0f0f0;
	flex-shrink: 0;
`;

const DrawerTitle = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
`;

const DrawerTitleText = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #232334;
`;

const CloseButton = styled.button`
	width: 26px;
	height: 26px;
	display: flex;
	align-items: center;
	justify-content: center;
	border: 1px solid #e0e0e0;
	border-radius: 50%;
	background: none;
	cursor: pointer;
	font-size: 13px;
	color: #666;
	padding: 0;
	&:hover {
		background: #f5f5f5;
	}
`;

const PersonRow = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 8px;
`;

const AvatarCircle = styled.div`
	width: 34px;
	height: 34px;
	border-radius: 50%;
	background: ${CSS_CONSTANTS.v2_branding_purple};
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 11px;
	font-weight: 700;
	color: white;
	flex-shrink: 0;
	overflow: hidden;
`;

const AvatarImage = styled.img`
	width: 100%;
	height: 100%;
	object-fit: cover;
`;

const PersonMeta = styled.div`
	flex: 1;
	min-width: 0;
`;

const PersonName = styled.div`
	font-size: 16px;
	font-weight: 350;
	color: #232334;
`;

const PersonContext = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 2px;
`;

const ContextLabel = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #232334;
`;

const TotalTime = styled.div`
	font-size: 16px;
	font-weight: 500;
	color: #232334;
`;

const ScrollArea = styled.div`
	overflow-y: auto;
	flex: 1;
	padding: 0;

	/* Hover-reveal scroll bar: hidden at rest, appears while the pointer is over the
	 * drawer's scroll area — matching the Timesheet Entries page behaviour. (PIKA-627) */
	${hoverRevealScrollbar}
`;

const ProjectRow = styled.div`
	border-bottom: 1px solid #f5f5f5;
	& + & {
		border-top: 1px solid #e0e0e0;
	}
`;

const ProjectHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 10px 18px;
	background: #f5f3f9;
	cursor: pointer;
	user-select: none;
	&:hover {
		background: #ede9f4;
	}
`;

const ProjectColorPill = styled.div<{color: string}>`
	background: ${({color}) => color};
	border-radius: 4px;
	padding: 4px 8px;
	font-size: 12px;
	font-weight: 350;
	color: #232334;
	flex-shrink: 0;
	letter-spacing: 0.3px;
`;

const ProjectName = styled.div`
	flex: 1;
	min-width: 0;
	overflow: hidden;
`;

const ProjectNamePrimary = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #232334;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const ProjectNameSub = styled.div`
	font-size: 12px;
	color: #6b6d7b;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	margin-top: 1px;
`;

const StatusChip = styled.div<{chipBg: string}>`
	width: 24px;
	height: 24px;
	border-radius: 4px;
	background: ${({chipBg}) => chipBg};
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	z-index: 1;
	position: relative;
`;

const ProjectTotalLabel = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: #6b6d7b;
	white-space: nowrap;
	font-variant-numeric: tabular-nums;
	flex-shrink: 0;
`;

const ChevronIcon = styled.div<{expanded: boolean}>`
	font-size: 9px;
	color: #aaa;
	flex-shrink: 0;
	transform: ${({expanded}) => (expanded ? 'rotate(0deg)' : 'rotate(180deg)')};
	transition: transform 150ms ease;
`;

const TimelineWrapper = styled.div`
	padding: 14px 18px 8px 24px;
	position: relative;
`;

const DashedLine = styled.div`
	position: absolute;
	left: 35px;
	top: 20px;
	bottom: 14px;
	border-left: 1.5px dashed #d4d4d4;
`;

const EventRow = styled.div`
	display: flex;
	gap: 10px;
	margin-bottom: 14px;
	position: relative;
`;

const EventContent = styled.div`
	flex: 1;
	min-width: 0;
`;

const EventActionLabel = styled.div`
	font-size: 14px;
	font-weight: 500;
	color: #1e1e1e;
	margin-bottom: 2px;
`;

/**
 * The extent qualifier that rides the action label line ("Approved · 3 days,
 * 11–15 May").
 *
 * Machine-generated, so it carries no border — the bordered box is reserved for
 * human-authored text (decline reasons, notes) so that a border means "a person
 * wrote this". Separation here is by weight and colour alone, which also keeps it
 * from competing with the actor name directly beneath it.
 */
const EventActionScope = styled.span`
	font-size: 12px;
	font-weight: 400;
	color: #6b6d7b;
`;

const EventActionScopeSeparator = styled.span`
	color: #c4c4cc;
	padding: 0 5px;
`;

const EventActor = styled.div`
	font-size: 14px;
	color: #232334;
	margin-bottom: 1px;
`;

const EventDate = styled.div`
	font-size: 12px;
	color: #6b6d7b;
	margin-bottom: 6px;
`;

const DayGroupWrapper = styled.div`
	& + & {
		margin-top: 8px;
		padding-top: 10px;
		border-top: 1px dashed #e8e8e8;
	}
`;

const DayLabel = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	font-size: 11px;
	font-weight: 600;
	color: #9ca3af;
	text-transform: uppercase;
	letter-spacing: 0.6px;
	padding-bottom: 8px;
	margin-left: 34px;
`;

/*
 * The bordered box marks human-authored text and nothing else. Decline reasons and
 * notes keep it; machine-generated content -- field diffs, invoice references -- is
 * carried by alignment and value contrast alone. That makes the border a semantic
 * signal ("a person wrote this") rather than decoration, and lets a reader tell the two
 * payload types apart at a glance without reading either. At most one bordered element
 * per row.
 */
const DiffGrid = styled.div`
	display: grid;
	grid-template-columns: 96px minmax(0, 1fr);
	gap: 6px;
	align-items: baseline;
	margin-top: 2px;
`;

const DiffLabel = styled.div`
	font-size: 12px;
	color: #9ca3af;
	line-height: 17px;
`;

const DiffValue = styled.div`
	font-size: 12px;
	line-height: 17px;
	word-break: break-word;
`;

const DiffOld = styled.span`
	color: #6b6d7b;
`;

/* Arrow at label weight so it fades before the values do under a squint test. */
const DiffArrow = styled.span`
	color: #c4c4cc;
	padding: 0 5px;
`;

const DiffNew = styled.span`
	color: #232334;
	font-weight: 500;
`;

/* Invoice references are machine output, so they lose the box the shipped drawer gave
 * them and render as a plain line. */
const InvoiceLine = styled.div`
	font-size: 12px;
	line-height: 17px;
	color: #232334;
	margin-top: 2px;
`;

const InvoiceLineMuted = styled.span`
	color: #6b6d7b;
`;

const ReasonBox = styled.div`
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	padding: 8px 10px;
	display: flex;
	align-items: flex-start;
	gap: 7px;
	background: #fafafa;
`;

const ReasonIcon = styled.div`
	font-size: 14px;
	color: #aaa;
	flex-shrink: 0;
	margin-top: 1px;
`;

const ReasonText = styled.div`
	font-size: 12px;
	color: #6b6d7b;
	line-height: 1.5;
`;

/**
 * Deep link to the invoice, styled to sit inside the ReasonBox alongside ReasonText.
 * A react-router Link rather than a bare anchor: it renders a real href (so
 * middle-click, cmd-click and "copy link address" all work — worth keeping on an
 * audit trail) while routing a plain click through the SPA instead of reloading it.
 */
const InvoiceLink = styled(Link)`
	font-size: 12px;
	font-weight: 500;
	line-height: 1.5;
	color: ${CSS_CONSTANTS.link_text_color};
	text-decoration: none;
	&:hover,
	&:focus {
		text-decoration: underline;
	}
`;

const EmptyState = styled.div`
	margin: 14px 18px;
	padding: 12px;
	border: 1px dashed #e0e0e0;
	border-radius: 6px;
	text-align: center;
	font-size: 11px;
	color: #aaa;
`;

/** Shown for time regs that predate audit logging — they have a status but no log entries. */
const NoHistoryPlaceholder = styled.div`
	margin: 0 0 10px 34px;
	padding: 10px 12px;
	border: 1px dashed #e8e8e8;
	border-radius: 6px;
	background: #fafafa;
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: #9ca3af;
`;

const LoadingState = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 40px;
	font-size: 13px;
	color: #aaa;
`;

const Backdrop = styled.div<{isOpen: boolean}>`
	display: ${({isOpen}) => (isOpen ? 'block' : 'none')};
	position: fixed;
	inset: 0;
	z-index: 199;
`;

/**
 * One timeline row. Shared by the span band and the day sections, so an event hoisted
 * out of its day renders identically to one that stayed.
 *
 * `siblings` is the band the row sits in, and is what decides whether the date prints:
 * it repeats only when the day changes from the row above.
 */
const renderTimelineEvent = (
	event: DisplayEvent,
	i: number,
	siblings: DisplayEvent[],
	keyPrefix: string,
	intl: ReturnType<typeof useIntl>
) => {
	const {entry, edits, regIndex, regTotal} = event;
	const config = getActionConfig(entry);
	const actorName = entry.actor ? `${entry.actor.firstName ?? ''} ${entry.actor.lastName ?? ''}`.trim() : 'System';
	const displayReason = getDisplayReason(entry.reason, entry.isBypassEdit);
	const invoiceDisplay = getInvoiceDisplay(entry);
	const batchScopeLabel = getBatchScopeLabel(entry, intl);
	const diffs = collapseEditBatch(edits);
	const noteText = isNoteEdit(entry) ? entry.newValue : null;

	// An edit targets one registration, so on a day holding several it
	// says which. A day holding one needs no ordinal.
	const ordinal =
		entry.eventType === 'FIELD_EDIT' && regTotal > 1
			? intl.formatMessage({id: 'time_approval.audit_log.entry_ordinal'}, {index: regIndex, total: regTotal})
			: null;

	// Print the date on the first row, then only when it moves. The
	// group header carries the registration's own date and these carry
	// event timestamps, and the two routinely disagree — so the rows
	// cannot inherit it, they can only stop repeating it.
	const eventDay = entry.createdAt ? moment.utc(entry.createdAt).format('YYYY-MM-DD') : null;
	const previous = siblings[i - 1];
	const previousDay = previous?.entry.createdAt ? moment.utc(previous.entry.createdAt).format('YYYY-MM-DD') : null;
	const showFullDate = i === 0 || eventDay !== previousDay;

	return (
		<EventRow key={entry.id ?? `${keyPrefix}-${i}`}>
			<StatusChip chipBg={config.chipBg}>
				<config.chipIcon size={16} />
			</StatusChip>
			<EventContent>
				<EventActionLabel>
					{intl.formatMessage({id: config.labelId})}
					{(batchScopeLabel || ordinal) && (
						<EventActionScope data-cy={batchScopeLabel ? 'audit-log-batch-scope' : 'audit-log-entry-ordinal'}>
							<EventActionScopeSeparator>·</EventActionScopeSeparator>
							{batchScopeLabel ?? ordinal}
						</EventActionScope>
					)}
				</EventActionLabel>
				<EventActor>{actorName}</EventActor>
				<EventDate>{formatEntryTime(entry.createdAt, showFullDate)}</EventDate>
				{diffs.length > 0 && (
					<DiffGrid data-cy="audit-log-diff-grid">
						{diffs.map((diff, d) => (
							<React.Fragment key={diff.id ?? `${diff.fieldName}-${d}`}>
								<DiffLabel>
									{diff.fieldName && EDIT_FIELD_LABEL_IDS[diff.fieldName]
										? intl.formatMessage({
												id: EDIT_FIELD_LABEL_IDS[diff.fieldName],
										  })
										: diff.fieldName}
								</DiffLabel>
								<DiffValue data-cy="audit-diff-row">
									<DiffOld>{formatEditValue(diff, 'old', intl)}</DiffOld>
									<DiffArrow>→</DiffArrow>
									<DiffNew>{formatEditValue(diff, 'new', intl)}</DiffNew>
								</DiffValue>
							</React.Fragment>
						))}
					</DiffGrid>
				)}
				{noteText && (
					<ReasonBox data-cy="audit-log-note">
						<ReasonIcon>
							<FeedbackIcon size={16} />
						</ReasonIcon>
						<ReasonText>{noteText}</ReasonText>
					</ReasonBox>
				)}
				{invoiceDisplay && (
					<InvoiceLine data-cy={INVOICE_ROW_CY[invoiceDisplay.kind]}>
						{invoiceDisplay.kind === 'link' ? (
							<InvoiceLink to={invoiceDisplay.href}>
								{intl.formatMessage(
									{id: 'time_approval.audit_log.invoice_reference'},
									{invoiceNumber: invoiceDisplay.invoiceNumber}
								)}
							</InvoiceLink>
						) : invoiceDisplay.kind === 'number' ? (
							intl.formatMessage(
								{id: 'time_approval.audit_log.invoice_reference'},
								{invoiceNumber: invoiceDisplay.invoiceNumber}
							)
						) : (
							<InvoiceLineMuted>
								{intl.formatMessage({
									id: 'time_approval.audit_log.invoice_deleted',
								})}
							</InvoiceLineMuted>
						)}
					</InvoiceLine>
				)}
				{displayReason && (
					<ReasonBox>
						<ReasonIcon>
							<FeedbackIcon size={16} />
						</ReasonIcon>
						<ReasonText>{displayReason}</ReasonText>
					</ReasonBox>
				)}
			</EventContent>
		</EventRow>
	);
};

// ---------------------------------------------------------------------------
// Sub-component: a single project accordion section
// ---------------------------------------------------------------------------
const ProjectSection: React.FC<{
	group: ProjectGroup;
	mode: 'week' | 'day';
	emptyMessageId: string;
	showTaskTotal: boolean;
	defaultExpanded: boolean;
}> = ({group, mode, emptyMessageId, showTaskTotal, defaultExpanded}) => {
	const intl = useIntl();
	const [expanded, setExpanded] = useState(defaultExpanded);

	const badge =
		group.approvalStatus && !PENDING_STATUSES.has(group.approvalStatus)
			? getApprovalStatusBadge(group.approvalStatus)
			: null;
	const statusChipProps = badge ? STATUS_CHIP_MAP[badge.color] ?? null : null;

	const pillColor = group.projectColor ?? CSS_CONSTANTS.v2_branding_purple;
	const pillLabel = group.customProjectId ?? (group.companyProjectId != null ? `P${group.companyProjectId}` : '—');

	// Keep only entries that map to a recognised workflow action. Skip day groups
	// that are newly created (PENDING/NOT_SUBMITTED) with no log history yet.
	const visibleDayGroups = group.dayGroups.filter(
		dg => dg.events.length > 0 || (dg.approvalStatus && !PENDING_STATUSES.has(dg.approvalStatus))
	);
	const hasSpanBand = group.spanEvents.length > 0;

	return (
		<ProjectRow>
			<ProjectHeader onClick={() => mode === 'week' && setExpanded(e => !e)}>
				<ProjectColorPill color={pillColor}>{pillLabel}</ProjectColorPill>
				<ProjectName>
					<ProjectNamePrimary title={group.taskName ?? group.projectName}>
						{group.taskName ?? group.projectName}
					</ProjectNamePrimary>
					{group.taskName && <ProjectNameSub title={group.projectName}>{group.projectName}</ProjectNameSub>}
				</ProjectName>
				{showTaskTotal && <ProjectTotalLabel>{formatMinutes(group.totalMinutes, intl)}</ProjectTotalLabel>}
				{statusChipProps && badge && (
					<TooltipContainer infoText={intl.formatMessage({id: badge.labelId})} growable>
						<StatusChip chipBg={statusChipProps.chipBg}>
							<statusChipProps.icon size={16} />
						</StatusChip>
					</TooltipContainer>
				)}
				{mode === 'week' && <ChevronIcon expanded={expanded}>▲</ChevronIcon>}
			</ProjectHeader>

			{expanded &&
				(visibleDayGroups.length === 0 && !hasSpanBand ? (
					<EmptyState>{intl.formatMessage({id: emptyMessageId})}</EmptyState>
				) : (
					<TimelineWrapper>
						<DashedLine />
						{hasSpanBand && (
							<DayGroupWrapper key="__span__">
								<DayLabel data-cy="audit-log-span-label">
									<span>{group.spanLabel}</span>
								</DayLabel>
								{group.spanEvents.map((event, i) =>
									renderTimelineEvent(event, i, group.spanEvents, '__span__', intl)
								)}
							</DayGroupWrapper>
						)}
						{visibleDayGroups.map(dayGroup => (
							<DayGroupWrapper key={dayGroup.dateKey}>
								<DayLabel>
									<span>{dayGroup.dateLabel}</span>
									<span style={{fontVariantNumeric: 'tabular-nums'}}>
										{formatMinutes(dayGroup.minutes, intl)}
									</span>
								</DayLabel>
								{dayGroup.events.length === 0 ? (
									<NoHistoryPlaceholder>
										<span style={{fontSize: 16, opacity: 0.4}}>🕓</span>
										{intl.formatMessage({id: 'time_approval.audit_log.no_history'})}
									</NoHistoryPlaceholder>
								) : (
									dayGroup.events.map((event, i) =>
										renderTimelineEvent(event, i, dayGroup.events, dayGroup.dateKey, intl)
									)
								)}
							</DayGroupWrapper>
						))}
					</TimelineWrapper>
				))}
		</ProjectRow>
	);
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const ApprovalLogDrawer: React.FC<ApprovalLogDrawerProps> = ({
	isOpen,
	onClose,
	personName,
	personProfilePictureId,
	contextLabel,
	totalMinutes,
	timeRegIds,
	mode,
	showTaskTotal = false,
}) => {
	const intl = useIntl();
	const {data, fetch, loading} = useForecastFetchQuery<QueryResponse, {ids: string[]}>(
		ApprovalLogDrawerQuery,
		true // clearDataOnFetch — stale data is replaced each open
	);

	// Fetch once when the drawer opens (or when the set of IDs changes)
	useEffect(() => {
		if (isOpen && timeRegIds.length > 0) {
			fetch({ids: timeRegIds});
		}
	}, [isOpen, timeRegIds.join(',')]); // eslint-disable-line

	// Close on Escape
	useEffect(() => {
		if (!isOpen) return;
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose();
		};
		document.addEventListener('keydown', handleKey);
		return () => document.removeEventListener('keydown', handleKey);
	}, [isOpen, onClose]);

	const timeRegs: TimeRegNode[] = (data?.nodes ?? []).filter((n): n is TimeRegNode => n != null && 'approvalLog' in n);

	const projectGroups = groupByProject(timeRegs);

	const emptyMessageId = mode === 'week' ? 'time_approval.audit_log.no_events_week' : 'time_approval.audit_log.no_events_day';

	const nameParts = personName.split(' ');
	const drawer = (
		<>
			<Backdrop isOpen={isOpen} onClick={onClose} />
			<DrawerContainer isOpen={isOpen} data-cy="approval-log-drawer">
				<DrawerHeader>
					<DrawerTitle>
						<DrawerTitleText>{intl.formatMessage({id: 'time_approval.audit_log.title'})}</DrawerTitleText>
						<CloseButton onClick={onClose} aria-label="Close">
							✕
						</CloseButton>
					</DrawerTitle>
					<PersonRow>
						<AvatarCircle>
							{personProfilePictureId ? (
								<AvatarImage
									src={profilePicSrc(personProfilePictureId)}
									alt={personName}
									crossOrigin="use-credentials"
									data-private
								/>
							) : (
								getInitials(nameParts[0] ?? null, nameParts.slice(1).join(' ') || null)
							)}
						</AvatarCircle>
						<PersonMeta>
							<PersonName>{personName}</PersonName>
							<PersonContext>
								<ContextLabel>{contextLabel}</ContextLabel>
								<TotalTime>{formatMinutes(totalMinutes, intl)}</TotalTime>
							</PersonContext>
						</PersonMeta>
					</PersonRow>
				</DrawerHeader>

				<ScrollArea>
					{loading ? (
						<LoadingState>Loading…</LoadingState>
					) : projectGroups.length === 0 ? (
						<EmptyState style={{margin: 18}}>{intl.formatMessage({id: emptyMessageId})}</EmptyState>
					) : (
						projectGroups.map((group, index) => (
							<ProjectSection
								key={group.projectId}
								group={group}
								mode={mode}
								emptyMessageId={emptyMessageId}
								showTaskTotal={showTaskTotal}
								defaultExpanded={index === 0}
							/>
						))
					)}
				</ScrollArea>
			</DrawerContainer>
		</>
	);

	return ReactDOM.createPortal(drawer, document.body);
};

export default ApprovalLogDrawer;
